import type { EventData } from '../../domain/message';
import type { SettingsAppStorage } from '../../domain/settings';
import { ENABLE_TOOLS_KEY, ENABLE_CRM_KEY, ENABLE_GCALENDAR_KEY, ENABLE_GMAIL_KEY } from '../../domain/settings';
import { mockUrlReplacer } from '../mockUrlReplacer';

import calendarsConfig from './calendars-config.json';
import dynamicsConfig from './dynamics-config.json';
import gmailConfig from './gmail-config.json';
import googleSheetsConfig from './google-sheets-config.json';
import hubspotConfig from './hubspot-config.json';
import linkedinConfig from './linkedin-config.json';
import salesLinkedinConfig from './linkedin-sales-config.json';
import salesforceConfig from './salesforce-config.json';
import salesforceLightningConfig from './salesforce-lightning-config.json';
import salesloftConfig from './salesloft-config.json';

type Config = {
  url: string;
  urlForMock?: string;
};

export const postConfig = () => {
  const config = [
    ...linkedinConfig,
    ...salesLinkedinConfig,
    ...salesloftConfig,
    ...hubspotConfig,
    ...salesforceConfig,
    ...salesforceLightningConfig,
    ...dynamicsConfig,
    ...calendarsConfig,
    ...gmailConfig,
    ...googleSheetsConfig,
  ];
  const enrichedConfig = config.flatMap((page: Config) => {
    if (!mockUrlReplacer.isMockable(page.urlForMock ?? page.url)) {
      return [page];
    }

    const mockedPage = { ...page, url: mockUrlReplacer.realToMock(page.urlForMock ?? page.url) };
    return [page, mockedPage];
  });

  window.parent.postMessage({ message: 'post config', config: enrichedConfig }, '*');
};

export const setSettingsItems = (items: Partial<SettingsAppStorage>) => {
  window.parent.postMessage({ message: 'set settings storage items', items }, '*');
};

export const removeSettingsItems = (keys: string | string[]) => {
  window.parent.postMessage({ message: 'remove settings storage', keys }, '*');
};

export const postBadgeVisibilityConfig = () => {
  const badgeVisibilityConfig = [
    ['calendar.google.com', ENABLE_GCALENDAR_KEY],
    ['outlook.live.com', ENABLE_TOOLS_KEY],
    ['app-\\w+.hubspot\\.com', ENABLE_CRM_KEY],
    ['.*\\.crm\\w+\\.dynamics\\.com', ENABLE_CRM_KEY],
    ['.*\\.force\\.com', ENABLE_CRM_KEY],
    ['.*\\.salesforce\\.com', ENABLE_CRM_KEY],
    ['app.salesloft.com', ENABLE_TOOLS_KEY],
    ['mail.google.com', ENABLE_GMAIL_KEY],
    ['docs.google.com/spreadsheets', ENABLE_TOOLS_KEY],
  ];
  window.parent.postMessage({ message: 'post badge visibility config', config: badgeVisibilityConfig }, '*');
};

export const initMessaging = () => {
  let queue: EventData[] = [];
  let messageHandler: ((event: EventData) => Promise<void>) | null = null;

  window.addEventListener('message', (event: MessageEvent<EventData>) => {
    if (messageHandler) {
      void messageHandler(event.data);
    } else {
      queue.push(event.data);
    }
  });

  return {
    registerHandler: (handler: (event: EventData) => Promise<void>) => {
      messageHandler = handler;
      queue.forEach((event) => void handler(event));
      queue = [];
    },
  };
};
